"use client"

import { cn } from "@/lib/utils"
import type { RailItem, RailAction } from "@/lib/rail/rail.types"
import { Button } from "@/components/ui/button"
import { Pin, ChevronDown, ChevronUp, X } from "lucide-react"
import { useState, useRef, useEffect } from "react"

interface AdvisorRailCardProps {
  item: RailItem
  onAction?: (action: RailAction) => void
  isGoalAnchor?: boolean
}

const severityDotColors = {
  neutral: "bg-blue-500/50",
  attention: "bg-blue-500/60",
  caution: "bg-amber-500/50",
  success: "bg-green-500/50",
  error: "bg-red-500/50",
}

export function AdvisorRailCard({ item, onAction, isGoalAnchor }: AdvisorRailCardProps) {
  const [thinkingExpanded, setThinkingExpanded] = useState(false)
  const [hasOverflow, setHasOverflow] = useState(false)
  const thinkingTextRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!item.thinkingContext || !thinkingTextRef.current) {
      setHasOverflow(false)
      return
    }

    const element = thinkingTextRef.current
    const hasHorizontalOverflow = element.scrollWidth > element.clientWidth
    setHasOverflow(hasHorizontalOverflow)
  }, [item.thinkingContext])

  const handleActionClick = (action: RailAction) => {
    console.log("[v0] AdvisorRailCard action:", action.intent, action.id)
    onAction?.(action)
  }

  const dismissAction = item.actions?.find((a) => a.intent === "dismiss")
  const decisionActions = item.actions?.filter((a) => a.intent !== "dismiss")

  return (
    <div
      className={cn(
        "rounded-xl",
        "bg-transparent",
        item.type === "advisor_question" ? "border border-[var(--border)]/60" : "border border-[var(--border)]",
        "hover:bg-[rgba(255,255,255,0.02)]",
        "transition-all duration-200",
        "p-3 space-y-3", // Increased spacing from 2.5 to 3
        isGoalAnchor && "sticky top-0 z-10 bg-[var(--surface-context)]",
      )}
    >
      {/* Header row */}
      {item.titleLabel && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={cn("h-1.5 w-1.5 rounded-full", severityDotColors[item.severity])} />
            <span className="text-[10px] font-semibold tracking-wider uppercase text-[var(--text-tertiary)] opacity-60">
              {item.titleLabel}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {dismissAction && (
              <div className="relative group/dismiss">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "h-6 w-6 p-0 min-w-[32px] min-h-[32px]",
                    "text-[var(--text-tertiary)] opacity-60",
                    "hover:opacity-80 hover:bg-[rgba(255,255,255,0.02)]",
                    "transition-all duration-200",
                  )}
                  onClick={() => handleActionClick(dismissAction)}
                  aria-label={dismissAction.label}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
                <div
                  className={cn(
                    "absolute bottom-full left-1/2 -translate-x-1/2 mb-2",
                    "px-2 py-1 rounded-md",
                    "bg-[var(--surface-context)] border border-[var(--border)]",
                    "text-xs text-[var(--text-primary)] whitespace-nowrap",
                    "opacity-0 group-hover/dismiss:opacity-100",
                    "transition-opacity duration-150",
                    "pointer-events-none",
                    "z-20",
                  )}
                  role="tooltip"
                >
                  {dismissAction.label}
                </div>
              </div>
            )}
            {isGoalAnchor && (
              <Pin className="h-3.5 w-3.5 text-[var(--text-tertiary)] opacity-60" aria-label="Pinned to top" />
            )}
          </div>
        </div>
      )}

      {/* Body */}
      <div className="space-y-2">
        {" "}
        {/* Increased spacing from 1.5 to 2 */}
        <p className="text-sm text-[var(--text-primary)] opacity-90 leading-snug">{item.primaryText}</p>
        {item.thinkingContext && (
          <div className="pt-1">
            {" "}
            {/* Increased spacing from 0.5 to 1 */}
            {hasOverflow ? (
              <button
                type="button"
                className="flex items-center justify-between w-full text-left group cursor-pointer"
                onClick={() => setThinkingExpanded(!thinkingExpanded)}
                aria-expanded={thinkingExpanded}
                aria-label={thinkingExpanded ? "Collapse thinking context" : "Expand thinking context"}
              >
                <span
                  ref={thinkingTextRef}
                  className={cn(
                    "text-xs text-[var(--text-tertiary)] opacity-60 leading-snug",
                    !thinkingExpanded && "truncate pr-2",
                  )}
                >
                  {item.thinkingContext}
                </span>
                <div className="flex-shrink-0 text-[var(--text-tertiary)] opacity-60 group-hover:opacity-80 transition-opacity">
                  {thinkingExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </div>
              </button>
            ) : (
              <span ref={thinkingTextRef} className="text-xs text-[var(--text-tertiary)] opacity-60 leading-snug block">
                {item.thinkingContext}
              </span>
            )}
          </div>
        )}
      </div>

      {decisionActions && decisionActions.length > 0 && (
        <div className="flex items-center gap-2 pt-1.5">
          {" "}
          {/* Increased spacing from 1 to 1.5 */}
          {decisionActions.map((action) => {
            const Icon = action.icon

            return (
              <div key={action.id} className="relative group/action">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "h-8 w-8 p-0 min-w-[32px] min-h-[32px]",
                    "text-[var(--text-secondary)] opacity-70",
                    "hover:opacity-100 hover:bg-[rgba(255,255,255,0.03)]",
                    "transition-all duration-200",
                    action.kind === "primary" && "hover:text-blue-500",
                  )}
                  onClick={() => handleActionClick(action)}
                  aria-label={action.label}
                >
                  {Icon && <Icon className="h-4 w-4" />}
                </Button>

                <div
                  className={cn(
                    "absolute bottom-full left-1/2 -translate-x-1/2 mb-2",
                    "px-2 py-1 rounded-md",
                    "bg-[var(--surface-context)] border border-[var(--border)]",
                    "text-xs text-[var(--text-primary)] whitespace-nowrap",
                    "opacity-0 group-hover/action:opacity-100",
                    "transition-opacity duration-150",
                    "pointer-events-none",
                    "z-20",
                  )}
                  role="tooltip"
                >
                  {action.label}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
